import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Check, FileText } from 'lucide-react';
import { Button, ErrorState, Skeleton, StatusBadge, SubBar } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { formatDate, formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { SignaturePad } from './SignaturePad';
import {
  useDocumentFile,
  useDocuments,
  useMySignature,
  uploadSignature,
  useSignDocument,
} from './api';

/**
 * נוהל אחד, ואם צריך גם החתימה עליו (PRD 5.10.1).
 *
 * החתימה היא הצהרה שקראת, ולכן היא יושבת בתחתית הנוהל ולא בראשו:
 * קודם קוראים.
 */
export function DocumentPage() {
  const { documentId = '' } = useParams();
  const { me } = useMe();
  // מתוך הרשימה, שממילא נטענה ומוחזקת במטמון. שאילתה נפרדת לנוהל
  // אחד היתה קריאה שנייה לאותם נתונים בדיוק.
  const documents = useDocuments();
  const mySignature = useMySignature(documentId);
  const sign = useSignDocument();
  const [blob, setBlob] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const found = documents.data?.find((row) => row.id === documentId) ?? null;
  const file = useDocumentFile(found?.file_path);

  if (documents.isPending) return <Skeleton className="m-4 h-64" />;
  if (documents.isError) return <ErrorState onRetry={() => void documents.refetch()} />;
  if (!found) return <Navigate to="/documents" replace />;

  const document = found;
  const signature = mySignature.data;
  const signedThisVersion = signature?.document_version === document.version;

  const submit = () => {
    if (!me?.employeeId) return;
    setBusy(true);
    setError(null);

    const upload = blob ? uploadSignature(me.employeeId, blob) : Promise.resolve(null);

    upload
      .then((path) => sign.mutateAsync({ documentId: document.id, signaturePath: path }))
      .then(() => void mySignature.refetch())
      .catch((caught) => setError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar
        title={document.title}
        subtitle={
          document.categories.length > 0
            ? document.categories.map((category) => category.name).join(' · ')
            : undefined
        }
      />

      <div className="grid gap-4 px-4 pb-8">
        <p className="m-0 text-label text-muted">
          גרסה {document.version} · עודכן {formatDate(document.updated_at)}
        </p>

        {document.body && (
          <p className="m-0 whitespace-pre-line text-body leading-relaxed">{document.body}</p>
        )}

        {file.data && (
          <a
            href={file.data}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-fit items-center gap-2 text-body font-medium text-ink"
          >
            <FileText size={20} strokeWidth={1.75} aria-hidden="true" />
            לפתיחת הקובץ המצורף
          </a>
        )}

        {document.requires_signature && (
          <section className="grid gap-3 rounded-md border border-line bg-raised p-3">
            <h2 className="m-0 font-display text-display-m font-semibold">חתימה</h2>

            {signedThisVersion ? (
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone="good">חתמת</StatusBadge>
                <span className="text-body-sm text-muted">
                  {signature?.signed_at && formatDateTime(signature.signed_at)}
                </span>
              </div>
            ) : (
              <>
                {signature && (
                  <p className="m-0 text-body-sm text-warn">
                    הנוהל עודכן מאז שחתמת. צריך לקרוא ולחתום מחדש.
                  </p>
                )}

                <p className="m-0 text-body-sm text-muted">
                  החתימה מאשרת שקראת את הנוהל והבנת אותו.
                </p>

                <SignaturePad onChange={setBlob} disabled={busy} />

                {error && (
                  <p role="alert" className="m-0 text-body-sm text-bad">
                    {error}
                  </p>
                )}

                <Button variant="primary" disabled={!blob || busy} onClick={submit}>
                  <Check size={20} strokeWidth={1.75} aria-hidden="true" />
                  {busy ? 'שומר…' : 'קראתי ואני חותם/ת'}
                </Button>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
